/**
 * Il Cruscotto di M4: **la fotografia della giornata**.
 *
 * Tre registri di dimensione decrescente, e l'ordine conta:
 *  1. quattro **tessere** — il colpo d'occhio, un numero grande ciascuna;
 *  2. **la giornata sulle macchine** — l'immagine (vedi `GiornataMacchine`);
 *  3. **la settimana dei ritiri** e **la tendenza** — il contesto, piccoli.
 *
 * La prima versione era cinque fasce di numeri tutti della stessa dimensione:
 * senza gerarchia niente si afferra, e il colpo d'occhio non esiste per
 * costruzione (rilievo di Enrico, 18-08).
 *
 * ⚠️ Le tessere dicono **previsto**, non fatto, e per tre stadi su quattro è
 * l'unica cosa che possiamo dire: il MES non ci manda l'avanzamento. La testata
 * lo dichiara — quando la sincronizzazione porterà il consuntivo questa stessa
 * vista diventerà «come sta andando».
 */

import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Factory, PackageCheck, Truck, Wheat } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { GiornataMacchine } from "@/components/m4/GiornataMacchine";
import { cn } from "@/lib/utils";
import { dec, kg, minutiInOre, num, pct } from "@/lib/numeri";
import type { M4GiornataResponse, M4SettimanaResponse } from "@/types/api";

type Tono = "neutro" | "attenzione" | "grave" | "buono";

const TONO_NUMERO: Record<Tono, string> = {
  neutro: "text-foreground",
  attenzione: "text-[var(--m4-manca-ink)]",
  grave: "text-destructive",
  buono: "text-emerald-700",
};

export function CruscottoM4({
  giornata,
  settimana,
}: {
  giornata: M4GiornataResponse;
  settimana: M4SettimanaResponse;
}) {
  const t = giornata.totali;
  const ritiriOggi =
    settimana.giorni.find((g) => g.e_il_giorno_scelto)?.ritiri ?? [];
  const palletOggi = ritiriOggi.reduce((a, r) => a + (r.pallet ? parseFloat(r.pallet) : 0), 0);
  const quotaSenzaMateriali = t.lavorazioni > 0 ? (t.senza_materiali / t.lavorazioni) * 100 : null;

  const oreEstrusori = giornata.tracce.filter((x) => x.gruppo === "estrusione");
  const oreConfezionatrici = giornata.tracce.filter((x) => x.gruppo === "confezionamento");

  return (
    <div className="space-y-3.5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tessera
          icona={Truck}
          eyebrow="Camion in uscita"
          numero={num(ritiriOggi.length)}
          unita={ritiriOggi.length === 1 ? "ritiro in agenda" : "ritiri in agenda"}
          tono={ritiriOggi.length === 0 ? "grave" : "neutro"}
          sotto={palletOggi > 0 ? `${num(palletOggi)} pallet dichiarati` : "nessun pallet dichiarato"}
          contesto={`${settimana.ritiri_totali} in tutta la settimana, ${settimana.ritiri_concordati} con la data confermata`}
          a="?tab=uscite"
          micro={settimana.giorni.map((g) => g.ritiri.length)}
          etichette={["lu", "ma", "me", "gi", "ve", "sa", "do"]}
        />
        <Tessera
          icona={PackageCheck}
          eyebrow="Confezionamento"
          numero={num(t.confezionamento_lavorazioni)}
          unita="lavorazioni"
          tono={t.confezionamento_lavorazioni === 0 ? "grave" : "neutro"}
          sotto={`${kg(t.confezionamento_kg)} · ${minutiInOre(t.confezionamento_minuti)} di linea`}
          contesto={`${oreConfezionatrici.filter((x) => x.durata_minuti > 0).length} confezionatrici su ${oreConfezionatrici.length} al lavoro`}
          a="?tab=confezionamento"
          micro={oreConfezionatrici.map((x) => x.durata_minuti)}
          etichette={oreConfezionatrici.map((x) => x.macchina.slice(0, 3))}
        />
        <Tessera
          icona={Factory}
          eyebrow="Produzione"
          numero={kg(t.estrusione_kg)}
          unita=""
          tono={t.estrusione_lavorazioni === 0 ? "grave" : "neutro"}
          sotto={`${t.estrusione_lavorazioni} lavorazioni · ${minutiInOre(t.estrusione_minuti)} di macchina`}
          contesto={`${oreEstrusori.filter((x) => x.durata_minuti > 0).length} estrusori su ${oreEstrusori.length} al lavoro`}
          a="?tab=produzione"
          micro={oreEstrusori.map((x) => x.durata_minuti)}
          etichette={oreEstrusori.map((x) => x.macchina.slice(0, 6))}
        />
        <Tessera
          icona={Wheat}
          eyebrow="Materie prime"
          numero={quotaSenzaMateriali === null ? "niente in piano" : pct(quotaSenzaMateriali)}
          numeroPiccolo={quotaSenzaMateriali === null}
          unita=""
          tono={
            quotaSenzaMateriali === null
              ? "neutro"
              : quotaSenzaMateriali > 60
                ? "grave"
                : quotaSenzaMateriali > 20
                  ? "attenzione"
                  : "buono"
          }
          sotto={
            quotaSenzaMateriali === null
              ? "nessuna lavorazione in piano"
              : `${t.senza_materiali} lavorazioni su ${t.lavorazioni} senza materiali`
          }
          contesto="Lo dice il MES riga per riga. Non è l'inventario: quello arriverà da Cube."
          a="?tab=materie"
          anello={quotaSenzaMateriali}
        />
      </div>

      <GiornataMacchine dati={giornata} />

      <SettimanaRitiri settimana={settimana} />
    </div>
  );
}

function Tessera({
  icona: Icona,
  eyebrow,
  numero,
  numeroPiccolo,
  unita,
  sotto,
  contesto,
  tono,
  a,
  micro,
  etichette,
  anello,
}: {
  icona: LucideIcon;
  eyebrow: string;
  numero: string;
  numeroPiccolo?: boolean;
  unita: string;
  sotto: string;
  contesto: string;
  tono: Tono;
  a: string;
  micro?: number[];
  etichette?: string[];
  anello?: number | null;
}) {
  const massimo = Math.max(...(micro ?? [0]), 1);
  return (
    <Link
      to={a}
      className="group flex flex-col gap-0.5 rounded-xl border bg-card p-4 transition-shadow hover:border-primary/30 hover:shadow-sm"
    >
      <div className="flex items-center justify-between">
        <span className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
          {eyebrow}
        </span>
        <Icona className="size-3.5 text-muted-foreground/70" />
      </div>
      <p
        className={cn(
          "mt-1.5 flex items-baseline gap-1.5 font-semibold tabular-nums tracking-[-0.035em]",
          numeroPiccolo ? "text-[22px] font-medium text-muted-foreground" : "text-[42px] leading-none",
          !numeroPiccolo && TONO_NUMERO[tono],
        )}
      >
        {numero}
        {unita && (
          <span className="text-[13px] font-medium tracking-normal text-muted-foreground">
            {unita}
          </span>
        )}
      </p>
      <p className="mt-0.5 text-xs text-foreground/80">{sotto}</p>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{contesto}</p>

      {anello !== undefined && anello !== null && (
        <div
          className="mt-2 grid size-14 place-items-center rounded-full"
          style={{
            background: `conic-gradient(var(--m4-manca) 0 ${anello}%, var(--muted) ${anello}% 100%)`,
          }}
        >
          <span className="grid size-10 place-items-center rounded-full bg-card text-[11px] font-semibold tabular-nums">
            {pct(anello)}
          </span>
        </div>
      )}

      {micro && micro.length > 0 && anello === undefined && (
        <div className="mt-2.5">
          <div className="flex h-8 items-end gap-[3px]">
            {micro.map((v, i) => (
              <span
                key={i}
                className={cn(
                  "flex-1 rounded-t-sm",
                  v === 0 ? "bg-muted" : "bg-[var(--m4-lavoro)]/45",
                )}
                style={{ height: v === 0 ? 3 : Math.max(4, (v / massimo) * 32) }}
              />
            ))}
          </div>
          {etichette && (
            <div className="mt-1 flex gap-[3px]">
              {etichette.map((e, i) => (
                <span
                  key={i}
                  className="flex-1 truncate text-center text-[8.5px] text-muted-foreground"
                >
                  {e}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      <span className="mt-2 flex items-center gap-1 text-[10.5px] font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
        Apri <ArrowRight className="size-3" />
      </span>
    </Link>
  );
}

const NOMI_GIORNO = ["lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica"];

/**
 * La settimana e non il giorno: in una giornata tipo ci sono zero-tre ritiri in
 * agenda, quindi un riquadro «oggi» sarebbe vuoto per costruzione.
 *
 * ⚠️ Niente corsie mattina/pomeriggio: `fascia_ritiro` esiste nel modello (è la
 * fascia del calendario di Marina) ed è **nulla su tutti** gli avvisi. Corsie
 * vuote avrebbero suggerito una precisione che non abbiamo.
 */
function SettimanaRitiri({ settimana }: { settimana: M4SettimanaResponse }) {
  return (
    <section className="rounded-xl border bg-card p-4">
      <h2 className="mb-2.5 text-[10.5px] font-semibold uppercase tracking-[0.095em] text-muted-foreground">
        La settimana dei ritiri{" "}
        <span className="font-normal normal-case tracking-normal">
          — quando i clienti hanno detto che vengono a prendere la merce
        </span>
      </h2>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-7">
        {settimana.giorni.map((g, i) => (
          <div
            key={g.giorno}
            className={cn(
              "flex min-h-[4.5rem] flex-col gap-1 rounded-lg border p-2",
              g.e_il_giorno_scelto && "border-primary ring-1 ring-inset ring-primary",
            )}
          >
            <p className="text-[10px] capitalize text-muted-foreground">
              {NOMI_GIORNO[i]} <b className="text-foreground">{g.giorno.slice(8, 10)}</b>
            </p>
            {g.ritiri.length === 0 ? (
              <p className="mt-auto text-[10px] text-muted-foreground/60">nessun ritiro</p>
            ) : (
              g.ritiri.map((r, j) => (
                <span
                  key={`${r.cliente}-${j}`}
                  title={`${r.cliente} — ${r.stato_etichetta}${r.pallet ? ` · ${dec(r.pallet)} pallet` : ""}`}
                  className={cn(
                    "truncate rounded border px-1.5 py-0.5 text-[10px] leading-tight",
                    r.concordato
                      ? "border-primary/25 bg-accent text-accent-foreground"
                      : "border-[var(--m4-manca)]/30 bg-[var(--m4-manca)]/10 text-[var(--m4-manca-ink)]",
                  )}
                >
                  {r.cliente}
                  <br />
                  {r.stato_etichetta}
                </span>
              ))
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap justify-between gap-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          {settimana.ritiri_totali === 0 && (
            <AlertTriangle className="size-3.5 text-[var(--m4-manca)]" />
          )}
          {settimana.ritiri_totali === 0
            ? "Nessun ritiro in agenda in tutta la settimana"
            : `${settimana.ritiri_totali} ritiri in agenda, di cui ${settimana.ritiri_concordati} con la data confermata o la NDP già emessa`}
        </span>
        <span>
          Non ci sono corsie mattina/pomeriggio: la fascia esiste nel modello ma non è
          compilata su nessun avviso
        </span>
      </div>
    </section>
  );
}
